// All landing-page UI copy. Fully editable, keyed by language.
// en = English, hi = हिन्दी, hinglish = Hinglish.

export const CONTENT = {
  en: {
    nav: {
      sample: "Sample Report",
      how: "How It Works",
      powers: "What Powers It",
      pricing: "Pricing",
      faq: "FAQ",
      cta: "Get Started",
    },
    hero: {
      badge: "Personalized Daily Timing Intelligence",
      titleA: "Kya Aap Sahi Kaam",
      titleHighlight: "Galat Samay Par Kar Rahe Hain?",
      sub: "Receive personalized daily guidance generated using your birth details, current planetary positions and Vedic astrology, delivered to your WhatsApp every morning at 7:00 AM.",
      ctaPrimary: "Get My Daily Guidance",
      ctaSecondary: "See Today's Sample Report",
      langLine: "Available in English • हिन्दी • Hinglish",
      trust: "Trusted by early users across India",
    },
    trust: [
      "Personalized Guidance",
      "Delivered Every Morning",
      "WhatsApp Delivery",
      "Powered by Vedic Astrology",
      "Private & Secure",
    ],
    whyTiming: {
      eyebrow: "The Idea",
      title: "Why Timing Matters",
      paras: [
        "Every day we make countless decisions. Some are small. Some shape our future.",
        "The same action can produce different outcomes depending on the timing.",
        "ShubhSamay helps you understand the nature of your day so you can approach important decisions with greater clarity, confidence and preparation.",
      ],
    },
    whyChoose: {
      eyebrow: "The Benefits",
      title: "Why People Choose ShubhSamay",
      sub: "Practical advantages you feel in your everyday decisions.",
      items: [
        { title: "Better Daily Decisions", desc: "Approach each choice with a clear sense of the day ahead." },
        { title: "Greater Clarity", desc: "Start the morning knowing what to prioritize and why." },
        { title: "Improved Focus", desc: "Spend energy on what truly matters, not on distractions." },
        { title: "Better Timing", desc: "Know the most supportive windows for important actions." },
        { title: "More Confidence", desc: "Move forward feeling prepared instead of uncertain." },
        { title: "Simple & Practical Guidance", desc: "Clear, actionable direction — no jargon, no confusion." },
      ],
    },
    whoFor: {
      eyebrow: "Made For You",
      title: "Made For Anyone Seeking Better Daily Clarity",
      sub: "Whether you're running a business, preparing for an important meeting, managing your studies or simply planning your day, ShubhSamay helps you approach every day with greater confidence and clarity.",
      roles: ["Business Owners", "Working Professionals", "Students", "Entrepreneurs", "Traders", "Freelancers"],
      roleDescs: ["Time your key decisions", "Plan your workday", "Focus & study better", "Act at the right moment", "Read the day's tempo", "Prioritize with clarity"],
    },
    sample: {
      eyebrow: "Experience It",
      title: "See Your Daily Report",
      sub: "Every subscriber receives a personalized report every morning on WhatsApp. Switch between English, हिन्दी and Hinglish to experience how your report will look.",
      note: "Generated using your birth details and current Vedic astrological conditions.",
    },
    how: {
      eyebrow: "Simple Setup",
      title: "How It Works",
      steps: [
        { title: "Share Your Birth Details", desc: "Provide your birth details once, in a simple and secure form." },
        { title: "Astrological Analysis", desc: "Our personalized Vedic astrology engine analyzes your daily planetary influences." },
        { title: "Daily Report Generated", desc: "Your personalized report is prepared, clearly and thoughtfully." },
        { title: "Delivered on WhatsApp", desc: "Receive your report every morning on WhatsApp at 7:00 AM." },
      ],
    },
    powers: {
      eyebrow: "The Intelligence Engine",
      title: "What Powers Your Daily Report",
      flow: [
        "Your Birth Details",
        "Current Planetary Positions",
        "Personalized Vedic Astrology Analysis",
        "AI Interpretation",
        "Simple Daily Guidance",
        "Delivered Every Morning on WhatsApp",
      ],
      sub: "Every report combines multiple Vedic astrology principles to create practical daily guidance that is easy to understand and act upon.",
      principles: [
        { title: "Dasha", desc: "The broader life-phase you're moving through, shaping your bigger themes." },
        { title: "Planetary Transits", desc: "How current planetary movements interact with your chart today." },
        { title: "Panchang", desc: "The quality and nature of the day itself, in the Vedic calendar." },
        { title: "Current Positions", desc: "Real-time planetary positions translated into practical timing." },
      ],
    },
    included: {
      eyebrow: "Every Morning",
      title: "Every Daily Report Includes",
      items: [
        "Daily Energy Score",
        "Decision Score",
        "Best Time Window",
        "Focus Areas",
        "Opportunity Analysis",
        "Watch-Out Moments",
        "Personalized Guidance",
        "Actionable Daily Suggestions",
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Simple & Affordable Pricing",
      sub: "Simple subscription with flexible renewal options.",
      monthly: { name: "Monthly Plan", badge: "Recommended", features: ["Personalized Daily Report", "Delivered on WhatsApp", "Best Time Window", "Available in 3 Languages"] },
      annual: {
        name: "Annual Plan",
        badge: "Most Popular",
        saveLabel: "Save",
        features: ["Personalized Daily Report", "Delivered on WhatsApp", "Best Time & Caution Windows", "Opportunity & Focus Analysis", "Available in 3 Languages"],
      },
      perYear: "every year",
      comingSoon: "Launching Soon",
      cta: "Start My Daily Guidance",
    },
    testimonials: {
      eyebrow: "Loved By Users",
      title: "What People Say About ShubhSamay",
      items: [
        { name: "Rajesh Kumar", role: "Business Owner", city: "Indore", rating: 5, text: "The timing insights are genuinely helpful. I now plan my most important meetings around the best windows and it has made my days calmer." },
        { name: "Neha Sharma", role: "Marketing Manager", city: "Pune", rating: 5, text: "I start my day only after reading my ShubhSamay report. It has helped me focus on what matters and avoid unnecessary stress." },
        { name: "Amit Verma", role: "Trader", city: "Kanpur", rating: 5, text: "Simple, practical and easy to follow. I love getting clear guidance every morning in plain language." },
      ],
    },
    faq: {
      eyebrow: "Questions",
      title: "Frequently Asked Questions",
      items: [
        { q: "What is ShubhSamay?", a: "ShubhSamay is a premium daily guidance service. Each morning you receive a personalized report on WhatsApp that helps you approach your day with better timing, clarity and confidence." },
        { q: "How is the report prepared?", a: "Your report is generated using your birth details, current planetary positions and personalized Vedic astrology analysis — then translated into simple, practical daily guidance." },
        { q: "Do I need astrology knowledge?", a: "Not at all. Every report is written in clear, everyday language. You simply read it and act on it — no prior knowledge required." },
        { q: "When will I receive my report?", a: "Your personalized report arrives on WhatsApp every morning at 7:00 AM, so you can plan your day right from the start." },
        { q: "Can I change my language?", a: "Yes. You can receive your report in English, हिन्दी or Hinglish, and switch anytime." },
        { q: "How do subscriptions work?", a: "Choose a monthly or annual plan. It's a simple subscription with flexible renewal options, and your details stay private and secure." },
        { q: "How is ShubhSamay different from a regular horoscope?", a: "A regular horoscope gives generic, zodiac-based predictions for everyone. ShubhSamay generates personalized daily guidance using your own birth details and the current Vedic astrological conditions — so it is specific to you, not a one-size-fits-all reading." },
      ],
    },
    finalCta: {
      titleLines: ["Better Timing.", "Better Decisions.", "Better Days."],
      sub: "Start receiving personalized daily guidance on WhatsApp every morning and approach each day with greater clarity and confidence.",
      cta: "Get My Daily Guidance",
    },
    footer: {
      tagline: "Personalized daily guidance powered by Vedic astrology and delivered on WhatsApp every morning.",
      columns: {
        product: { title: "Product", links: ["Sample Report", "How It Works", "What Powers It", "Pricing", "FAQ"] },
        company: { title: "Company", links: ["About Us", "Contact Us"] },
        legal: { title: "Legal", links: ["Privacy Policy", "Terms & Conditions", "Refund Policy", "Disclaimer"] },
      },
      supportTitle: "Support",
      supportEmail: "Email Support",
      whatsapp: "WhatsApp Support",
      supportHours: "We reply within 24–48 business hours.",
      rights: "All rights reserved.",
    },
    common: { getStarted: "Get Started" },
  },

  hi: {
    nav: {
      sample: "सैंपल रिपोर्ट",
      how: "यह कैसे काम करता है",
      powers: "यह किससे बनता है",
      pricing: "मूल्य",
      faq: "सामान्य प्रश्न",
      cta: "शुरू करें",
    },
    hero: {
      badge: "व्यक्तिगत दैनिक समय मार्गदर्शन",
      titleA: "Kya Aap Sahi Kaam",
      titleHighlight: "Galat Samay Par Kar Rahe Hain?",
      sub: "अपने जन्म विवरण, वर्तमान ग्रह स्थितियों और वैदिक ज्योतिष के आधार पर व्यक्तिगत दैनिक मार्गदर्शन पाएँ, हर सुबह 7:00 बजे सीधे आपके WhatsApp पर।",
      ctaPrimary: "मेरा दैनिक मार्गदर्शन पाएँ",
      ctaSecondary: "आज की सैंपल रिपोर्ट देखें",
      langLine: "उपलब्ध: English • हिन्दी • Hinglish",
      trust: "भारत भर के शुरुआती उपयोगकर्ताओं का भरोसा",
    },
    trust: [
      "व्यक्तिगत मार्गदर्शन",
      "हर सुबह डिलीवरी",
      "WhatsApp पर डिलीवरी",
      "व्यक्तिगत वैदिक ज्योतिष",
      "निजी और सुरक्षित",
    ],
    whyTiming: {
      eyebrow: "विचार",
      title: "समय क्यों मायने रखता है",
      paras: [
        "हर दिन हम अनगिनत निर्णय लेते हैं। कुछ छोटे, कुछ हमारा भविष्य तय करते हैं।",
        "वही काम, समय के अनुसार अलग-अलग परिणाम दे सकता है।",
        "ShubhSamay आपको अपने दिन की प्रकृति समझने में मदद करता है, ताकि आप महत्वपूर्ण निर्णय अधिक स्पष्टता, आत्मविश्वास और तैयारी के साथ ले सकें।",
      ],
    },
    whyChoose: {
      eyebrow: "लाभ",
      title: "लोग ShubhSamay क्यों चुनते हैं",
      sub: "व्यावहारिक लाभ जो आप अपने रोज़मर्रा के निर्णयों में महसूस करते हैं।",
      items: [
        { title: "बेहतर दैनिक निर्णय", desc: "हर निर्णय को दिन की स्पष्ट समझ के साथ लें।" },
        { title: "अधिक स्पष्टता", desc: "सुबह से पता हो कि किसे प्राथमिकता देनी है और क्यों।" },
        { title: "बेहतर फोकस", desc: "अपनी ऊर्जा वहीं लगाएँ जो सचमुच मायने रखता है।" },
        { title: "बेहतर समय", desc: "महत्वपूर्ण कार्यों के लिए सबसे अनुकूल समय जानें।" },
        { title: "अधिक आत्मविश्वास", desc: "अनिश्चितता के बजाय तैयारी के साथ आगे बढ़ें।" },
        { title: "सरल और व्यावहारिक मार्गदर्शन", desc: "स्पष्ट, actionable दिशा — कोई जटिलता नहीं।" },
      ],
    },
    whoFor: {
      eyebrow: "आपके लिए",
      title: "हर उस व्यक्ति के लिए जो बेहतर दैनिक स्पष्टता चाहता है",
      sub: "चाहे आप व्यवसाय चला रहे हों, किसी ज़रूरी मीटिंग की तैयारी कर रहे हों, पढ़ाई संभाल रहे हों या बस अपना दिन योजना बना रहे हों — ShubhSamay आपको हर दिन अधिक आत्मविश्वास और स्पष्टता के साथ आगे बढ़ने में मदद करता है।",
      roles: ["व्यवसायी", "कामकाजी पेशेवर", "विद्यार्थी", "उद्यमी", "ट्रेडर्स", "फ्रीलांसर"],
      roleDescs: ["सही समय पर निर्णय", "अपना दिन योजना बनाएँ", "बेहतर फोकस व अध्ययन", "सही पल पर कदम", "दिन की गति समझें", "स्पष्टता से प्राथमिकता"],
    },
    sample: {
      eyebrow: "अनुभव करें",
      title: "अपनी दैनिक रिपोर्ट देखें",
      sub: "हर सब्सक्राइबर को हर सुबह WhatsApp पर व्यक्तिगत रिपोर्ट मिलती है। English, हिन्दी और Hinglish के बीच स्विच करके देखें कि आपकी रिपोर्ट कैसी दिखेगी।",
      note: "आपके जन्म विवरण और वर्तमान वैदिक ज्योतिषीय स्थितियों के आधार पर तैयार।",
    },
    how: {
      eyebrow: "आसान सेटअप",
      title: "यह कैसे काम करता है",
      steps: [
        { title: "अपने जन्म विवरण साझा करें", desc: "एक बार, एक सरल और सुरक्षित फॉर्म में अपने जन्म विवरण दें।" },
        { title: "ज्योतिषीय विश्लेषण", desc: "हमारा व्यक्तिगत वैदिक ज्योतिष इंजन आपके दैनिक ग्रह प्रभावों का विश्लेषण करता है।" },
        { title: "दैनिक रिपोर्ट तैयार", desc: "आपकी व्यक्तिगत रिपोर्ट स्पष्टता के साथ तैयार की जाती है।" },
        { title: "WhatsApp पर डिलीवरी", desc: "हर सुबह 7:00 बजे अपनी रिपोर्ट WhatsApp पर पाएँ।" },
      ],
    },
    powers: {
      eyebrow: "इंटेलिजेंस इंजन",
      title: "आपकी दैनिक रिपोर्ट किससे बनती है",
      flow: [
        "आपके जन्म विवरण",
        "वर्तमान ग्रह स्थितियाँ",
        "व्यक्तिगत वैदिक ज्योतिष विश्लेषण",
        "AI व्याख्या",
        "सरल दैनिक मार्गदर्शन",
        "हर सुबह WhatsApp पर डिलीवरी",
      ],
      sub: "हर रिपोर्ट कई वैदिक ज्योतिष सिद्धांतों को मिलाकर व्यावहारिक दैनिक मार्गदर्शन बनाती है, जिसे समझना और अपनाना आसान है।",
      principles: [
        { title: "दशा", desc: "जीवन का वह बड़ा चरण जिससे आप गुज़र रहे हैं, जो बड़े विषय तय करता है।" },
        { title: "ग्रह गोचर", desc: "वर्तमान ग्रह गति आज आपकी कुंडली से कैसे संवाद करती है।" },
        { title: "पंचांग", desc: "वैदिक कैलेंडर में दिन की गुणवत्ता और प्रकृति।" },
        { title: "वर्तमान स्थितियाँ", desc: "रीयल-टाइम ग्रह स्थितियाँ, व्यावहारिक समय में अनुवादित।" },
      ],
    },
    included: {
      eyebrow: "हर सुबह",
      title: "हर दैनिक रिपोर्ट में शामिल",
      items: [
        "दैनिक ऊर्जा स्कोर",
        "निर्णय स्कोर",
        "सर्वोत्तम समय विंडो",
        "फोकस क्षेत्र",
        "अवसर विश्लेषण",
        "सावधानी के क्षण",
        "व्यक्तिगत मार्गदर्शन",
        "व्यावहारिक दैनिक सुझाव",
      ],
    },
    pricing: {
      eyebrow: "मूल्य",
      title: "सरल और किफ़ायती मूल्य",
      sub: "लचीले नवीनीकरण विकल्पों के साथ सरल सब्सक्रिप्शन।",
      monthly: { name: "मासिक प्लान", badge: "अनुशंसित", features: ["व्यक्तिगत दैनिक रिपोर्ट", "WhatsApp पर डिलीवरी", "सर्वोत्तम समय विंडो", "3 भाषाओं में उपलब्ध"] },
      annual: {
        name: "वार्षिक प्लान",
        badge: "सबसे लोकप्रिय",
        saveLabel: "बचत",
        features: ["व्यक्तिगत दैनिक रिपोर्ट", "WhatsApp पर डिलीवरी", "सर्वोत्तम एवं सावधानी विंडो", "अवसर एवं फोकस विश्लेषण", "3 भाषाओं में उपलब्ध"],
      },
      perYear: "हर साल",
      comingSoon: "जल्द ही उपलब्ध",
      cta: "मेरा दैनिक मार्गदर्शन शुरू करें",
    },
    testimonials: {
      eyebrow: "उपयोगकर्ताओं की पसंद",
      title: "लोग ShubhSamay के बारे में क्या कहते हैं",
      items: [
        { name: "राजेश कुमार", role: "व्यवसायी", city: "इंदौर", rating: 5, text: "समय संबंधी सुझाव वाकई मददगार हैं। अब मैं अपनी ज़रूरी मीटिंग सबसे अच्छे समय पर रखता हूँ और मेरे दिन शांत रहते हैं।" },
        { name: "नेहा शर्मा", role: "मार्केटिंग मैनेजर", city: "पुणे", rating: 5, text: "मैं अपना दिन ShubhSamay रिपोर्ट पढ़ने के बाद ही शुरू करती हूँ। इसने मुझे ज़रूरी चीज़ों पर फोकस करने में मदद की है।" },
        { name: "अमित वर्मा", role: "ट्रेडर", city: "कानपुर", rating: 5, text: "सरल, व्यावहारिक और आसान। हर सुबह सहज भाषा में स्पष्ट मार्गदर्शन मिलना बहुत अच्छा लगता है।" },
      ],
    },
    faq: {
      eyebrow: "प्रश्न",
      title: "अक्सर पूछे जाने वाले प्रश्न",
      items: [
        { q: "ShubhSamay क्या है?", a: "ShubhSamay एक प्रीमियम दैनिक मार्गदर्शन सेवा है। हर सुबह आपको WhatsApp पर व्यक्तिगत रिपोर्ट मिलती है जो आपके दिन को बेहतर समय, स्पष्टता और आत्मविश्वास के साथ शुरू करने में मदद करती है।" },
        { q: "रिपोर्ट कैसे तैयार होती है?", a: "आपकी रिपोर्ट आपके जन्म विवरण, वर्तमान ग्रह स्थितियों और व्यक्तिगत वैदिक ज्योतिष विश्लेषण से बनती है — फिर उसे सरल, व्यावहारिक मार्गदर्शन में बदला जाता है।" },
        { q: "क्या मुझे ज्योतिष का ज्ञान चाहिए?", a: "बिल्कुल नहीं। हर रिपोर्ट सरल, रोज़मर्रा की भाषा में लिखी जाती है। बस पढ़ें और अपनाएँ।" },
        { q: "मुझे रिपोर्ट कब मिलेगी?", a: "आपकी व्यक्तिगत रिपोर्ट हर सुबह 7:00 बजे WhatsApp पर आती है, ताकि आप अपना दिन शुरुआत से ही योजना बना सकें।" },
        { q: "क्या मैं भाषा बदल सकता हूँ?", a: "हाँ। आप अपनी रिपोर्ट English, हिन्दी या Hinglish में पा सकते हैं और कभी भी बदल सकते हैं।" },
        { q: "सब्सक्रिप्शन कैसे काम करता है?", a: "मासिक या वार्षिक प्लान चुनें। यह लचीले नवीनीकरण विकल्पों के साथ सरल सब्सक्रिप्शन है, और आपकी जानकारी निजी व सुरक्षित रहती है।" },
        { q: "ShubhSamay एक सामान्य राशिफल से कैसे अलग है?", a: "सामान्य राशिफल सभी के लिए एक जैसी, राशि-आधारित भविष्यवाणी देता है। ShubhSamay आपके अपने जन्म विवरण और वर्तमान वैदिक ज्योतिषीय स्थितियों के आधार पर व्यक्तिगत दैनिक मार्गदर्शन तैयार करता है — जो केवल आपके लिए होता है, सबके लिए एक जैसा नहीं।" },
      ],
    },
    finalCta: {
      titleLines: ["बेहतर समय।", "बेहतर निर्णय।", "बेहतर दिन।"],
      sub: "हर सुबह WhatsApp पर व्यक्तिगत दैनिक मार्गदर्शन पाना शुरू करें और हर दिन अधिक स्पष्टता व आत्मविश्वास के साथ आगे बढ़ें।",
      cta: "मेरा दैनिक मार्गदर्शन पाएँ",
    },
    footer: {
      tagline: "वैदिक ज्योतिष द्वारा संचालित व्यक्तिगत दैनिक मार्गदर्शन, हर सुबह WhatsApp पर।",
      columns: {
        product: { title: "प्रोडक्ट", links: ["सैंपल रिपोर्ट", "यह कैसे काम करता है", "यह किससे बनता है", "मूल्य", "सामान्य प्रश्न"] },
        company: { title: "कंपनी", links: ["हमारे बारे में", "संपर्क करें"] },
        legal: { title: "कानूनी", links: ["गोपनीयता नीति", "नियम एवं शर्तें", "रिफंड नीति", "अस्वीकरण"] },
      },
      supportTitle: "सहायता",
      supportEmail: "ईमेल सहायता",
      whatsapp: "WhatsApp सहायता",
      supportHours: "हम 24–48 कार्य घंटों में उत्तर देते हैं।",
      rights: "सर्वाधिकार सुरक्षित।",
    },
    common: { getStarted: "शुरू करें" },
  },

  hinglish: {
    nav: {
      sample: "Sample Report",
      how: "How It Works",
      powers: "What Powers It",
      pricing: "Pricing",
      faq: "FAQ",
      cta: "Get Started",
    },
    hero: {
      badge: "Personalized Daily Timing Intelligence",
      titleA: "Kya Aap Sahi Kaam",
      titleHighlight: "Galat Samay Par Kar Rahe Hain?",
      sub: "Apne birth details, current planetary positions aur Vedic astrology ke basis par personalized daily guidance paayein, har subah 7:00 AM par seedha aapke WhatsApp par.",
      ctaPrimary: "Mera Daily Guidance Paayein",
      ctaSecondary: "Aaj Ki Sample Report Dekhein",
      langLine: "Available in English • हिन्दी • Hinglish",
      trust: "India bhar ke early users ka bharosa",
    },
    trust: [
      "Personalized Guidance",
      "Har Subah Delivery",
      "WhatsApp Delivery",
      "Personalized Vedic Astrology",
      "Private & Secure",
    ],
    whyTiming: {
      eyebrow: "The Idea",
      title: "Timing Kyun Matter Karti Hai",
      paras: [
        "Har din hum countless decisions lete hain. Kuch chhote, kuch humara future banate hain.",
        "Wahi action, timing ke hisaab se alag results de sakta hai.",
        "ShubhSamay aapko apne din ki nature samajhne mein madad karta hai, taaki aap important decisions zyada clarity, confidence aur preparation ke saath le sakein.",
      ],
    },
    whyChoose: {
      eyebrow: "The Benefits",
      title: "Log ShubhSamay Kyun Choose Karte Hain",
      sub: "Practical benefits jo aap apne rozana ke decisions mein feel karte hain.",
      items: [
        { title: "Better Daily Decisions", desc: "Har choice ko din ki clear samajh ke saath lein." },
        { title: "Greater Clarity", desc: "Subah se pata ho kise priority deni hai aur kyun." },
        { title: "Improved Focus", desc: "Energy wahin lagayein jo sach mein matter karta hai." },
        { title: "Better Timing", desc: "Important actions ke liye sabse supportive windows jaanein." },
        { title: "More Confidence", desc: "Uncertainty ke bajaye preparation ke saath aage badhein." },
        { title: "Simple & Practical Guidance", desc: "Clear, actionable direction — no jargon, no confusion." },
      ],
    },
    whoFor: {
      eyebrow: "Made For You",
      title: "Har Us Insaan Ke Liye Jo Better Daily Clarity Chahta Hai",
      sub: "Chahe aap business chala rahe hon, ek important meeting ki taiyari kar rahe hon, studies manage kar rahe hon ya bas apna din plan kar rahe hon — ShubhSamay aapko har din zyada confidence aur clarity ke saath aage badhne mein madad karta hai.",
      roles: ["Business Owners", "Working Professionals", "Students", "Entrepreneurs", "Traders", "Freelancers"],
      roleDescs: ["Sahi time par decisions", "Apna workday plan karein", "Behtar focus & study", "Sahi pal par kadam", "Din ki tempo samjhein", "Clarity se prioritize"],
    },
    sample: {
      eyebrow: "Experience It",
      title: "Apni Daily Report Dekhein",
      sub: "Har subscriber ko har subah WhatsApp par personalized report milti hai. English, हिन्दी aur Hinglish ke beech switch karke dekhein ki aapki report kaisi dikhegi.",
      note: "Aapke birth details aur current Vedic astrological conditions ke aadhaar par generated.",
    },
    how: {
      eyebrow: "Simple Setup",
      title: "How It Works",
      steps: [
        { title: "Apne Birth Details Share Karein", desc: "Ek baar, ek simple aur secure form mein apne birth details dein." },
        { title: "Astrological Analysis", desc: "Humara personalized Vedic astrology engine aapke daily planetary influences analyze karta hai." },
        { title: "Daily Report Generate", desc: "Aapki personalized report clarity ke saath tayaar hoti hai." },
        { title: "WhatsApp Par Delivery", desc: "Har subah 7:00 AM par apni report WhatsApp par paayein." },
      ],
    },
    powers: {
      eyebrow: "The Intelligence Engine",
      title: "Aapki Daily Report Kis Se Banti Hai",
      flow: [
        "Aapke Birth Details",
        "Current Planetary Positions",
        "Personalized Vedic Astrology Analysis",
        "AI Interpretation",
        "Simple Daily Guidance",
        "Har Subah WhatsApp Par Delivery",
      ],
      sub: "Har report multiple Vedic astrology principles ko combine karke practical daily guidance banati hai, jise samajhna aur follow karna easy hai.",
      principles: [
        { title: "Dasha", desc: "Life ka wo bada phase jisse aap guzar rahe hain, jo bade themes tay karta hai." },
        { title: "Planetary Transits", desc: "Current planetary movements aaj aapki chart se kaise interact karti hain." },
        { title: "Panchang", desc: "Vedic calendar mein din ki quality aur nature." },
        { title: "Current Positions", desc: "Real-time planetary positions, practical timing mein translate." },
      ],
    },
    included: {
      eyebrow: "Har Subah",
      title: "Har Daily Report Mein Shaamil",
      items: [
        "Daily Energy Score",
        "Decision Score",
        "Best Time Window",
        "Focus Areas",
        "Opportunity Analysis",
        "Watch-Out Moments",
        "Personalized Guidance",
        "Actionable Daily Suggestions",
      ],
    },
    pricing: {
      eyebrow: "Pricing",
      title: "Simple & Affordable Pricing",
      sub: "Flexible renewal options ke saath simple subscription.",
      monthly: { name: "Monthly Plan", badge: "Recommended", features: ["Personalized Daily Report", "Delivered on WhatsApp", "Best Time Window", "3 Languages Mein Available"] },
      annual: {
        name: "Annual Plan",
        badge: "Most Popular",
        saveLabel: "Save",
        features: ["Personalized Daily Report", "Delivered on WhatsApp", "Best & Caution Windows", "Opportunity & Focus Analysis", "3 Languages Mein Available"],
      },
      perYear: "har saal",
      comingSoon: "Jald Hi Launching",
      cta: "Mera Daily Guidance Shuru Karein",
    },
    testimonials: {
      eyebrow: "Loved By Users",
      title: "Log ShubhSamay Ke Baare Mein Kya Kehte Hain",
      items: [
        { name: "Rajesh Kumar", role: "Business Owner", city: "Indore", rating: 5, text: "Timing insights genuinely helpful hain. Ab main apni important meetings best windows par plan karta hoon aur mere din calmer ho gaye hain." },
        { name: "Neha Sharma", role: "Marketing Manager", city: "Pune", rating: 5, text: "Main apna din ShubhSamay report padhne ke baad hi shuru karti hoon. Isne mujhe important cheezon par focus karne mein madad ki hai." },
        { name: "Amit Verma", role: "Trader", city: "Kanpur", rating: 5, text: "Simple, practical aur easy to follow. Har subah plain language mein clear guidance milna bahut accha lagta hai." },
      ],
    },
    faq: {
      eyebrow: "Questions",
      title: "Frequently Asked Questions",
      items: [
        { q: "ShubhSamay kya hai?", a: "ShubhSamay ek premium daily guidance service hai. Har subah aapko WhatsApp par personalized report milti hai jo aapke din ko better timing, clarity aur confidence ke saath shuru karne mein madad karti hai." },
        { q: "Report kaise prepare hoti hai?", a: "Aapki report aapke birth details, current planetary positions aur personalized Vedic astrology analysis se banti hai — fir simple, practical guidance mein translate ki jaati hai." },
        { q: "Kya mujhe astrology knowledge chahiye?", a: "Bilkul nahi. Har report clear, everyday language mein likhi jaati hai. Bas padhein aur follow karein." },
        { q: "Report kab milegi?", a: "Aapki personalized report har subah 7:00 AM par WhatsApp par aati hai, taaki aap apna din shuruaat se hi plan kar sakein." },
        { q: "Kya main language change kar sakta hoon?", a: "Haan. Aap apni report English, हिन्दी ya Hinglish mein paa sakte hain aur kabhi bhi switch kar sakte hain." },
        { q: "Subscriptions kaise kaam karti hain?", a: "Monthly ya annual plan chunein. Yeh flexible renewal options ke saath simple subscription hai, aur aapki details private & secure rehti hain." },
        { q: "ShubhSamay ek regular horoscope se kaise alag hai?", a: "Regular horoscope sabke liye generic, zodiac-based predictions deta hai. ShubhSamay aapke apne birth details aur current Vedic astrological conditions ke basis par personalized daily guidance generate karta hai — jo sirf aapke liye hota hai, sabke liye ek jaisa nahi." },
      ],
    },
    finalCta: {
      titleLines: ["Better Timing.", "Better Decisions.", "Better Days."],
      sub: "Har subah WhatsApp par personalized daily guidance paana shuru karein aur har din zyada clarity aur confidence ke saath aage badhein.",
      cta: "Mera Daily Guidance Paayein",
    },
    footer: {
      tagline: "Vedic astrology se powered personalized daily guidance, har subah WhatsApp par.",
      columns: {
        product: { title: "Product", links: ["Sample Report", "How It Works", "What Powers It", "Pricing", "FAQ"] },
        company: { title: "Company", links: ["About Us", "Contact Us"] },
        legal: { title: "Legal", links: ["Privacy Policy", "Terms & Conditions", "Refund Policy", "Disclaimer"] },
      },
      supportTitle: "Support",
      supportEmail: "Email Support",
      whatsapp: "WhatsApp Support",
      supportHours: "Hum 24–48 business hours mein reply karte hain.",
      rights: "All rights reserved.",
    },
    common: { getStarted: "Get Started" },
  },
};
